import { useEffect, useRef } from "react";
import type { Control, FieldErrors, FieldValues, Path } from "react-hook-form";
import { useFormState } from "react-hook-form";
import { toast } from "sonner";

const FORM_ERRORS_TOAST_ID = "form-validation-errors";

export interface FieldGroup {
	name: string;
	fields: string[];
}

export interface FormSection {
	name: string;
	groups: FieldGroup[];
}

export interface UseFormErrorToastsOptions<T extends FieldValues> {
	fieldOrder?: Path<T>[];
	formSections?: FormSection[];
}

/**
 * Hook that displays form validation errors as a single persistent toast
 * with a bulleted list of all errors, optionally grouped by form section.
 *
 * @param control - The form control from react-hook-form
 * @param options - Optional configuration including field order or form sections for grouping
 */
export function useFormErrorToasts<T extends FieldValues>(
	control: Control<T>,
	options?: UseFormErrorToastsOptions<T>,
) {
	const { errors, isSubmitted } = useFormState({ control });
	const prevErrorKeyRef = useRef<string>("");

	// Flatten errors and compute a stable key for change detection
	const flatErrors = flattenErrors(errors);
	const errorEntries = Object.entries(flatErrors);
	const errorKey = errorEntries.map(([k, msg]) => `${k}:${msg}`).sort().join("|");

	useEffect(() => {
		// Only show toast after first submit attempt
		if (!isSubmitted) return;

		// Only update toast if errors changed
		if (errorKey === prevErrorKeyRef.current) return;
		prevErrorKeyRef.current = errorKey;

		if (errorKey === "") {
			toast.dismiss(FORM_ERRORS_TOAST_ID);
			return;
		}

		const errorMap = new Map(errorEntries);

		// Render grouped or flat errors
		const content = options?.formSections
			? renderGroupedErrors(errorMap, options.formSections)
			: renderFlatErrors(errorMap, options?.fieldOrder);

		toast.error(content, {
			id: FORM_ERRORS_TOAST_ID,
			duration: Infinity,
			closeButton: true,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [errorKey, isSubmitted]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			toast.dismiss(FORM_ERRORS_TOAST_ID);
		};
	}, []);
}

function renderFlatErrors<T extends FieldValues>(
	errorMap: Map<string, string>,
	fieldOrder?: Path<T>[],
) {
	let errorMessages = Array.from(errorMap.values());

	if (fieldOrder) {
		const orderMap = new Map(fieldOrder.map((field, index) => [field, index]));
		const entries = Array.from(errorMap.entries());
		entries.sort(([keyA], [keyB]) => {
			const indexA = orderMap.get(keyA as Path<T>) ?? Number.MAX_SAFE_INTEGER;
			const indexB = orderMap.get(keyB as Path<T>) ?? Number.MAX_SAFE_INTEGER;
			return indexA - indexB;
		});
		errorMessages = entries.map(([, msg]) => msg);
	}

	return (
		<div className="flex flex-col gap-1">
			<span className="font-semibold">Please fix the following:</span>
			<ul className="list-disc pl-4 text-sm">
				{errorMessages.map((msg) => (
					<li key={msg}>{msg}</li>
				))}
			</ul>
		</div>
	);
}

function renderGroupedErrors(
	errorMap: Map<string, string>,
	formSections: FormSection[],
) {
	const sectionsWithErrors: Array<{
		name: string;
		groups: Array<{ name: string; errors: string[] }>;
	}> = [];

	for (const section of formSections) {
		const groupsWithErrors: Array<{ name: string; errors: string[] }> = [];

		for (const group of section.groups) {
			const groupErrors: string[] = [];
			for (const field of group.fields) {
				const error = errorMap.get(field);
				if (error) {
					groupErrors.push(error);
				}
			}
			if (groupErrors.length > 0) {
				groupsWithErrors.push({ name: group.name, errors: groupErrors });
			}
		}

		if (groupsWithErrors.length > 0) {
			sectionsWithErrors.push({ name: section.name, groups: groupsWithErrors });
		}
	}

	return (
		<div className="flex flex-col gap-1">
			<span className="font-semibold">Please fix the following:</span>
			<div className="text-sm space-y-2">
				{sectionsWithErrors.map((section) => (
					<div key={section.name}>
						<span className="font-medium">{section.name}</span>
						{section.groups.map((group, groupIndex) => (
							<ul
								key={group.name || groupIndex}
								className={group.name ? "list-disc pl-4" : "list-disc pl-4"}
							>
								{group.name && (
									<li>
										<span className="font-medium">{group.name}</span>
										<ul className="list-disc pl-4">
											{group.errors.map((error) => (
												<li key={error}>{error}</li>
											))}
										</ul>
									</li>
								)}
								{!group.name &&
									group.errors.map((error) => <li key={error}>{error}</li>)}
							</ul>
						))}
					</div>
				))}
			</div>
		</div>
	);
}

/**
 * Flattens nested field errors into a simple key-message map
 */
function flattenErrors(errors: FieldErrors, prefix = ""): Record<string, string> {
	const result: Record<string, string> = {};

	for (const [key, value] of Object.entries(errors)) {
		const fullKey = prefix ? `${prefix}.${key}` : key;

		if (value?.message && typeof value.message === "string") {
			result[fullKey] = value.message;
		} else if (value && typeof value === "object" && !("message" in value)) {
			Object.assign(result, flattenErrors(value as FieldErrors, fullKey));
		}
	}

	return result;
}
